/** Shared post-dictation feedback contract for the desktop and web surfaces. */
export type DictationFeedbackPlatform = 'web' | 'desktop';
export type DictationFeedbackSurface = 'widget' | 'logs';
type DictationFeedbackConfirmationSurface = 'desktop_widget' | 'web_bubble';

export type DictationFeedbackContext = {
  dictationId: string | null;
  platform: DictationFeedbackPlatform;
};

export type DictationFeedbackReason =
  | 'wrong_word_or_name'
  | 'command_issue'
  | 'custom_vocab_issue'
  | 'content_missing'
  | 'number_dose_or_date_wrong'
  | 'other';

export type DictationFeedbackSubmission =
  | (DictationFeedbackContext & { score: 'positive' })
  | (DictationFeedbackContext & {
      score: 'negative';
      reasons: DictationFeedbackReason[];
      userFeedback?: string;
    });

export interface DictationFeedbackCallbacks {
  onSubmitFeedback?: (feedback: DictationFeedbackSubmission) => void | Promise<void>;
  onRequestNegativeFeedback?: (context: DictationFeedbackContext) => void;
}

export type DictationFeedbackRating = 'thumb_up' | 'thumb_down';

export type DictationFeedbackApiRequest = {
  feedback: {
    rating: DictationFeedbackRating;
    reasons: Array<{ reason: DictationFeedbackReason }>;
    user_feedback: string | null;
  };
};

export type DictationFeedbackApiResponse = {
  rating: DictationFeedbackRating;
  reasons: Array<{ reason: DictationFeedbackReason }>;
  user_feedback: string | null;
  updated_at: string;
};

/** Safe client-side cohort labels returned by transcription-service. */
export const DICTATION_FEEDBACK_EXPERIMENT_VARIANTS = [
  'A',
  'B',
  'non-eng-A',
  'non-eng-B',
  'multilingual-model',
  'non-eng-default',
  'default',
] as const;

export type DictationFeedbackExperimentVariant =
  (typeof DICTATION_FEEDBACK_EXPERIMENT_VARIANTS)[number];
export type DictationFeedbackRegion = 'au' | 'uk' | 'us';

export type DictationFeedbackAnalyticsContext = {
  surface: DictationFeedbackSurface;
  experimentVariant?: DictationFeedbackExperimentVariant;
  inputLanguages: string[];
  outputLanguages: string[];
  region?: DictationFeedbackRegion;
};

export type DictationFeedbackSubmittedEvent = {
  dictation_id: string;
  platform: DictationFeedbackPlatform;
  surface: DictationFeedbackSurface;
  experiment_variant?: DictationFeedbackExperimentVariant;
  input_languages: string[];
  output_languages: string[];
  region?: DictationFeedbackRegion;
  feedback_state: DictationFeedbackSubmission['score'];
  rating: DictationFeedbackRating;
  reasons: DictationFeedbackReason[];
  has_feedback_text: boolean;
};

export type DictationFeedbackAnalyticsPorts = {
  /** The initial post-dictation thumbs prompt became visible. */
  trackPromptViewed: () => void;
  /** The negative-feedback reason picker became visible. */
  trackReasonPickerViewed: () => void;
  /** The user left the reason picker through its "Not now" action. */
  trackNotNowClicked: () => void;
  /** The user opened Dictate history from the post-submission confirmation. */
  trackSeeHistoryClicked: (surface: DictationFeedbackConfirmationSurface) => void;
  /** The logs reason popover became visible. */
  trackLogsReasonPickerViewed: () => void;
  /** The user dismissed the logs reason popover through Cancel or Close. */
  trackLogsCancelClicked: () => void;
  trackSubmitted: (event: DictationFeedbackSubmittedEvent) => void;
};
